import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import {
  LIST_POLICY_ALIASES,
  LIST_POLICY_PRIMITIVES,
  SAVE_POLICY_ALIAS,
  DELETE_POLICY_ALIAS
} from '../userAccounts/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  formatOrganizationalAliasLabel,
  isOrganizationalAliasName,
  mergeOrganizationalAliases
} from '../userAccounts/organizationalAliases';

type AliasRecord = {
  id: number | null;
  alias: string;
  type?: string | null;
  description?: string | null;
  active?: boolean | null;
  policies?: string[] | null;
};

type UserAccountRequestOption = {
  id: number;
  optionType: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
};

type UserAccountRequestOptionsResponse = {
  roleOptions: UserAccountRequestOption[];
  organizationOptions: UserAccountRequestOption[];
};

const LIST_USER_ACCOUNT_REQUEST_OPTIONS = gql`
  query PermissionAliasRequestOptions {
    roleOptions: userAccountRequestOptions(optionType: "role") {
      id
      optionType
      label
      sortOrder
      isActive
    }
    organizationOptions: userAccountRequestOptions(optionType: "organization") {
      id
      optionType
      label
      sortOrder
      isActive
    }
  }
`;

type AliasType = 'group' | 'policy' | 'legacy' | 'hidden' | 'organizational';

const emptyAlias = {
  id: null as number | null,
  alias: '',
  type: 'group' as AliasType,
  description: '',
  active: true,
  policies: [] as string[]
};

const normalizeAliasType = (value: unknown): AliasType | '' => {
  const normalized = `${value || ''}`.trim().toLowerCase();
  if (
    normalized === 'group' ||
    normalized === 'policy' ||
    normalized === 'legacy' ||
    normalized === 'hidden' ||
    normalized === 'organizational'
  ) {
    return normalized;
  }
  return '';
};

const resolveAliasType = (alias: AliasRecord): AliasType => {
  const explicitType = normalizeAliasType(alias?.type);
  if (explicitType) {
    return explicitType;
  }
  const normalizedAlias = `${alias?.alias || ''}`.trim().toLowerCase();
  if (normalizedAlias.startsWith('legacy:')) {
    return 'legacy';
  }
  if (normalizedAlias.startsWith('hidden:')) {
    return 'hidden';
  }
  if (isOrganizationalAliasName(alias?.alias)) {
    return 'organizational';
  }
  return 'group';
};

const aliasTypeLabel: Record<AliasType, string> = {
  group: 'Group',
  policy: 'Feature',
  legacy: 'Legacy',
  hidden: 'Hidden',
  organizational: 'Organizational'
};

const aliasTypeBadgeClass: Record<AliasType, string> = {
  group: 'border border-blue-200 bg-blue-100 text-blue-800',
  policy: 'border border-emerald-200 bg-emerald-100 text-emerald-800',
  legacy: 'border border-amber-200 bg-amber-100 text-amber-800',
  hidden: 'border border-slate-200 bg-slate-100 text-slate-700',
  organizational: 'border border-violet-200 bg-violet-100 text-violet-800'
};

const getNamespace = (alias: AliasRecord) => {
  const aliasType = resolveAliasType(alias);
  if (aliasType === 'organizational') {
    return 'organizational';
  }
  const value = `${alias.alias || ''}`.trim();
  if (!value) {
    return 'other';
  }
  const namespace = value.split(':')[0];
  const trimmed = `${namespace || ''}`.trim();
  if (!trimmed) {
    return 'other';
  }
  return trimmed.toLowerCase();
};

const toSafeId = (value: string) => {
  const cleaned = `${value || ''}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const trimmed = cleaned.replace(/(^-|-$)/g, '');
  if (!trimmed) {
    return 'item';
  }
  return trimmed;
};

const buildPolicyPreview = (policies?: string[] | null) => {
  if (!Array.isArray(policies) || policies.length === 0) {
    return 'No policies';
  }
  const trimmed = policies.map((policy) => `${policy || ''}`.trim()).filter((policy) => policy.length > 0);
  if (trimmed.length === 0) {
    return 'No policies';
  }
  const preview = trimmed.slice(0, 2).join(', ');
  const remaining = trimmed.length - 2;
  if (remaining > 0) {
    return `${preview} +${remaining}`;
  }
  return preview;
};

const truncateText = (value: string, maxLength: number) => {
  const trimmed = `${value || ''}`.trim();
  if (!trimmed) {
    return '—';
  }
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, Math.max(maxLength - 3, 0))}...`;
};

const normalizeAliasKey = (value: unknown) => `${value || ''}`.trim().toLowerCase();

const getAliasDisplayLabel = (alias: AliasRecord) => {
  if (resolveAliasType(alias) !== 'organizational') {
    return alias.alias;
  }
  return formatOrganizationalAliasLabel(alias.alias);
};

const getEditableAliasType = (aliasName: string, aliasType: AliasType | ''): AliasType => {
  if (isOrganizationalAliasName(aliasName)) {
    return 'organizational';
  }
  return aliasType || 'group';
};

export const PermissionAliases: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<{ listPolicyAliases: AliasRecord[] }>(
    LIST_POLICY_ALIASES,
    { fetchPolicy: 'cache-and-network' }
  );
  const { data: optionData } = useQuery<UserAccountRequestOptionsResponse>(
    LIST_USER_ACCOUNT_REQUEST_OPTIONS,
    { fetchPolicy: 'cache-and-network' }
  );
  const { data: primitivesData } = useQuery<{ listPolicyPrimitives: string[] }>(
    LIST_POLICY_PRIMITIVES,
    { fetchPolicy: 'cache-first' }
  );
  const [saveAlias] = useMutation(SAVE_POLICY_ALIAS);
  const [deleteAlias] = useMutation(DELETE_POLICY_ALIAS);

  const [formState, setFormState] = useState(emptyAlias);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isNewAlias, setIsNewAlias] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openNamespaces, setOpenNamespaces] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [manualPolicyEntry, setManualPolicyEntry] = useState('');

  const organizationOptions = useMemo(() => {
    const rows = Array.isArray(optionData?.organizationOptions) ? optionData?.organizationOptions || [] : [];
    return rows
      .filter((row) => row?.isActive !== false && `${row?.label || ''}`.trim().length > 0)
      .sort((left, right) => {
        const sortDelta = Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0);
        if (sortDelta !== 0) {
          return sortDelta;
        }
        return `${left?.label || ''}`.localeCompare(`${right?.label || ''}`);
      })
      .map((row) => `${row?.label || ''}`.trim());
  }, [optionData?.organizationOptions]);

  const roleOptions = useMemo(() => {
    const rows = Array.isArray(optionData?.roleOptions) ? optionData?.roleOptions || [] : [];
    return rows
      .filter((row) => row?.isActive !== false && `${row?.label || ''}`.trim().length > 0)
      .sort((left, right) => {
        const sortDelta = Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0);
        if (sortDelta !== 0) {
          return sortDelta;
        }
        return `${left?.label || ''}`.localeCompare(`${right?.label || ''}`);
      })
      .map((row) => `${row?.label || ''}`.trim());
  }, [optionData?.roleOptions]);

  const aliasList = useMemo(() => {
    const aliases = Array.isArray(data?.listPolicyAliases) ? data?.listPolicyAliases || [] : [];
    return mergeOrganizationalAliases(aliases, organizationOptions, roleOptions).map((alias) => ({
      ...alias,
      id: alias.id ?? null
    })) as AliasRecord[];
  }, [data?.listPolicyAliases, organizationOptions, roleOptions]);

  const primitiveOptions = useMemo(() => {
    const primitives = primitivesData?.listPolicyPrimitives;
    if (!Array.isArray(primitives)) {
      return [];
    }
    return [...primitives]
      .map((policy) => `${policy || ''}`.trim())
      .filter((policy) => policy.length > 0)
      .sort((a, b) => a.localeCompare(b));
  }, [primitivesData]);

  const filteredAliases = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      return aliasList;
    }
    return aliasList.filter((alias) => {
      const aliasName = `${alias.alias || ''}`.toLowerCase();
      if (aliasName.includes(trimmed)) {
        return true;
      }
      const displayLabel = getAliasDisplayLabel(alias).toLowerCase();
      if (displayLabel.includes(trimmed)) {
        return true;
      }
      const description = `${alias.description || ''}`.toLowerCase();
      if (description.includes(trimmed)) {
        return true;
      }
      const policies = Array.isArray(alias.policies) ? alias.policies : [];
      return policies.some((policy) => `${policy || ''}`.toLowerCase().includes(trimmed));
    });
  }, [aliasList, searchTerm]);

  const namespaceGroups = useMemo(() => {
    const groups = new Map<string, AliasRecord[]>();
    filteredAliases.forEach((alias) => {
      const namespace = getNamespace(alias);
      const existing = groups.get(namespace) ?? [];
      existing.push(alias);
      groups.set(namespace, existing);
    });
    return Array.from(groups.entries())
      .map(([namespace, aliases]) => ({
        namespace,
        aliases: aliases.sort((a, b) => a.alias.localeCompare(b.alias))
      }))
      .sort((a, b) => a.namespace.localeCompare(b.namespace));
  }, [filteredAliases]);

  const namespaceKeys = useMemo(() => namespaceGroups.map((group) => group.namespace), [namespaceGroups]);

  const availablePolicies = useMemo(() => {
    if (primitiveOptions.length === 0) {
      return [];
    }
    const selected = new Set(formState.policies);
    return primitiveOptions.filter((policy) => !selected.has(policy));
  }, [primitiveOptions, formState.policies]);

  const selectedPolicies = useMemo(() => {
    if (!Array.isArray(formState.policies)) {
      return [];
    }
    const unique = Array.from(new Set(formState.policies));
    return unique.sort((a, b) => a.localeCompare(b));
  }, [formState.policies]);

  useEffect(() => {
    if (aliasList.length === 0) {
      setFormState(emptyAlias);
      return;
    }
    if (`${formState.alias || ''}`.trim()) {
      return;
    }
    if (isNewAlias) {
      return;
    }
    const first = aliasList[0];
    setFormState({
      id: first.id,
      alias: first.alias,
      type: resolveAliasType(first),
      description: first.description || '',
      active: first.active !== false,
      policies: Array.isArray(first.policies) ? [...first.policies] : []
    });
  }, [aliasList, formState.id, isNewAlias]);

  useEffect(() => {
    if (namespaceKeys.length === 0) {
      return;
    }
    if (openNamespaces.length > 0) {
      return;
    }
    setOpenNamespaces(namespaceKeys);
  }, [namespaceKeys, openNamespaces.length]);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      return;
    }
    setOpenNamespaces(namespaceKeys);
  }, [searchTerm, namespaceKeys]);

  const handleSelectAlias = (alias: AliasRecord) => {
    setIsNewAlias(false);
    setManualPolicyEntry('');
    setFormState({
      id: alias.id,
      alias: alias.alias,
      type: resolveAliasType(alias),
      description: alias.description || '',
      active: alias.active !== false,
      policies: Array.isArray(alias.policies) ? [...alias.policies] : []
    });
    setIsEditorOpen(true);
  };

  const handleNewAlias = () => {
    setIsNewAlias(true);
    setManualPolicyEntry('');
    setFormState({ ...emptyAlias, policies: [] });
    setIsEditorOpen(true);
  };

  const handleCopyPoliciesFromAlias = (alias: AliasRecord) => {
    const policies = Array.isArray(alias.policies) ? alias.policies : [];
    const trimmed = policies
      .map((policy) => `${policy || ''}`.trim())
      .filter((policy) => policy.length > 0);
    setFormState((prev) => {
      const merged = [...(prev.policies || []), ...trimmed];
      const unique = Array.from(new Set(merged));
      return { ...prev, policies: unique };
    });
  };

  const addPolicyToAlias = (policy: string) => {
    const normalizedPolicy = `${policy || ''}`.trim();
    if (!normalizedPolicy) {
      return false;
    }
    if (formState.policies.includes(normalizedPolicy)) {
      return false;
    }
    setFormState((prev) => ({
      ...prev,
      policies: [...prev.policies, normalizedPolicy]
    }));
    return true;
  };

  const handleAddManualPolicy = () => {
    const normalizedPolicy = `${manualPolicyEntry || ''}`.trim();
    if (!normalizedPolicy) {
      toast.error('Policy string is required');
      return;
    }

    const didAdd = addPolicyToAlias(normalizedPolicy);
    if (!didAdd) {
      toast.error('Policy is already included');
      return;
    }

    setManualPolicyEntry('');
  };

  const handleRemovePolicy = (policy: string) => {
    setFormState((prev) => ({
      ...prev,
      policies: prev.policies.filter((item) => item !== policy)
    }));
  };

  const handleSave = async () => {
    const aliasName = `${formState.alias || ''}`.trim();
    if (!aliasName) {
      toast.error('Group name is required');
      return;
    }

    setIsSaving(true);

    try {
      const result = await saveAlias({
        variables: {
          alias: {
            alias: aliasName,
            type: getEditableAliasType(aliasName, normalizeAliasType(formState.type)),
            description: formState.description || null,
            active: formState.active,
            policies: formState.policies
          }
        }
      });
      if (!result?.data?.savePolicyAlias?.success) {
        toast.error(result?.data?.savePolicyAlias?.message || 'Failed to save group');
        return;
      }
      const aliasId = result?.data?.savePolicyAlias?.aliasId;
      const refetchResult = await refetch();
      const aliases = refetchResult.data?.listPolicyAliases || [];
      let updated = null as AliasRecord | null;
      if (aliasId) {
        updated = aliases.find((alias) => alias.id === aliasId) || null;
      }
      if (!updated) {
        updated = aliases.find((alias) => normalizeAliasKey(alias.alias) === normalizeAliasKey(aliasName)) || null;
      }
      if (updated) {
        handleSelectAlias(updated);
      }
      if (!updated && aliasId) {
        setFormState((prev) => ({ ...prev, id: aliasId }));
        setIsNewAlias(false);
      }
      if (!updated && !aliasId) {
        setIsNewAlias(false);
      }
      toast.success('Group saved');
    } catch (error) {
      console.error('Failed to save alias:', error);
      toast.error('Failed to save alias');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formState.id) {
      return;
    }

    setIsDeleting(true);

    try {
      const result = await deleteAlias({
        variables: { aliasId: formState.id }
      });
      if (!result?.data?.deletePolicyAlias?.success) {
        toast.error(result?.data?.deletePolicyAlias?.message || 'Failed to delete group');
        return;
      }
      await refetch();
      setFormState({ ...emptyAlias, policies: [] });
      setIsNewAlias(false);
      setIsEditorOpen(false);
      toast.success('Group deleted');
    } catch (error) {
      console.error('Failed to delete alias:', error);
      toast.error('Failed to delete alias');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditorOpenChange = (open: boolean) => {
    setIsEditorOpen(open);
    if (open) {
      return;
    }
    setManualPolicyEntry('');
    if (!isNewAlias) {
      return;
    }
    setIsNewAlias(false);
  };

  const isLockedOrganizationalAlias = !isNewAlias && isOrganizationalAliasName(formState.alias);
  const selectedAliasType = getEditableAliasType(formState.alias, normalizeAliasType(formState.type));

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading groups...</div>;
  }

  if (error) {
    return <div className="p-6 text-sm text-red-600">Failed to load groups.</div>;
  }

  return (
      <div className="h-full p-4 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Policy Groups</h2>
        <p className="text-xs text-gray-500">Manage reusable policy bundles</p>
      </div>

      <div className="w-full">
        <Card>
          <CardHeader className="space-y-2 py-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="text-sm font-medium text-gray-700">Groups</CardTitle>
                <CardDescription className="text-xs">Select a group to edit</CardDescription>
              </div>
              <Button
                id="alias-manager-new-alias"
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleNewAlias}
              >
                New
              </Button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label htmlFor="alias-manager-search-input" className="text-xs font-medium text-gray-500">
                Search
              </label>
              <Input
                id="alias-manager-search-input"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search groups or policies"
                className="h-8 text-sm"
              />
            </div>
          </CardHeader>
          <CardContent className="flex h-[72vh] flex-col gap-2">
            <div className="flex-1 overflow-y-auto">
              {aliasList.length === 0 && (
                <p className="text-xs text-gray-500">No groups yet.</p>
              )}
              {aliasList.length > 0 && namespaceGroups.length === 0 && (
                <p className="text-xs text-gray-500">No groups match this search.</p>
              )}
              {namespaceGroups.length > 0 && (
                <Accordion
                  type="multiple"
                  value={openNamespaces}
                  onValueChange={setOpenNamespaces}
                  className="space-y-2"
                >
                  {namespaceGroups.map((group) => {
                    const safeNamespace = toSafeId(group.namespace);
                    const label = group.namespace === 'other' ? 'Other' : group.namespace;
                    return (
                      <AccordionItem key={group.namespace} value={group.namespace}>
                        <AccordionTrigger
                          id={`alias-manager-namespace-${safeNamespace}`}
                          className="px-2 py-2 text-xs text-gray-800"
                        >
                          <span className="flex items-center gap-2">
                            <span className="capitalize">{label}</span>
                            <span className="text-xs text-gray-400">{group.aliases.length}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-2">
                          <div className="divide-y divide-gray-100">
                            {group.aliases.map((alias) => {
                              const preview = buildPolicyPreview(alias.policies);
                              const isActive = normalizeAliasKey(formState.alias) === normalizeAliasKey(alias.alias);
                              const aliasDisplayLabel = getAliasDisplayLabel(alias);
                              const aliasKey = toSafeId(alias.alias);
                              return (
                                <button
                                  id={`alias-manager-select-${aliasKey}`}
                                  key={alias.alias}
                                  type="button"
                                  onClick={() => handleSelectAlias(alias)}
                                  className={`w-full px-2 py-1.5 text-left text-xs leading-tight transition-colors ${
                                    isActive
                                      ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-500'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="grid gap-2 text-xs text-gray-500 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,80px)]">
                                    <span className="flex min-w-0 items-center gap-2 break-words text-xs font-semibold text-gray-900">
                                      <span className="min-w-0 break-words">{aliasDisplayLabel}</span>
                                      <span className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${aliasTypeBadgeClass[resolveAliasType(alias)]}`}>
                                        {aliasTypeLabel[resolveAliasType(alias)]}
                                      </span>
                                    </span>
                                    <span className="min-w-0 truncate">
                                      {truncateText(alias.description || '', 50)}
                                    </span>
                                    <span className="min-w-0 truncate">
                                      {truncateText(preview, 50)}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      {alias.active === false ? 'Inactive' : 'Active'}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isEditorOpen} onOpenChange={handleEditorOpenChange}>
        <DialogContent className="w-[96vw] sm:w-[96vw] max-w-none sm:max-w-[96vw] h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="space-y-0">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <DialogTitle>{formState.id ? 'Edit Group' : 'New Group'}</DialogTitle>
                <DialogDescription className="text-xs">Update group details and included policies.</DialogDescription>
              </div>
              {formState.id && (
                <Button
                  id="permission-aliases-dialog-delete-button"
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Group'}
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)] flex-1 overflow-hidden">
            <Card className="flex flex-col overflow-hidden">
              <CardHeader className="py-3">
              <CardTitle className="text-sm">Group Details</CardTitle>
              <CardDescription className="text-xs">Basics and status for the selected group.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 overflow-y-auto">
                <div className="space-y-1">
                  <label htmlFor="alias-manager-name" className="text-xs font-medium text-gray-500">
                    Group Name
                  </label>
                  <Input
                    id="alias-manager-name"
                    value={isLockedOrganizationalAlias ? formatOrganizationalAliasLabel(formState.alias) : formState.alias}
                    onChange={(event) => setFormState((prev) => ({ ...prev, alias: event.target.value }))}
                    placeholder="legacy:fist:*"
                    className="h-8 text-sm"
                    disabled={isLockedOrganizationalAlias}
                  />
                  {selectedAliasType === 'organizational' && (
                    <p className="text-xs text-gray-500">
                      Organizational aliases come from the active organization and role option lists.
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label htmlFor="alias-manager-type" className="text-xs font-medium text-gray-500">
                    Alias Type
                  </label>
                  <Select
                    value={selectedAliasType}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, type: (normalizeAliasType(value) || 'group') as AliasType }))
                    }
                    disabled={selectedAliasType === 'organizational'}
                  >
                    <SelectTrigger id="alias-manager-type" className="h-8 text-sm">
                      <SelectValue placeholder="Alias type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="group">Group</SelectItem>
                      <SelectItem value="policy">Feature</SelectItem>
                      {selectedAliasType === 'organizational' && (
                        <SelectItem value="organizational">Organizational</SelectItem>
                      )}
                      <SelectItem value="legacy">Legacy</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <label htmlFor="alias-manager-description" className="text-xs font-medium text-gray-500">
                    Description
                  </label>
                  <Textarea
                    id="alias-manager-description"
                    value={formState.description}
                    onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Short description of the group"
                    className="min-h-[80px] text-sm"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="alias-manager-active"
                    type="checkbox"
                    checked={formState.active}
                    onChange={(event) => setFormState((prev) => ({ ...prev, active: event.target.checked }))}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="alias-manager-active" className="text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </CardContent>
            </Card>

            <Card className="flex flex-col overflow-hidden">
              <CardHeader className="py-3">
              <CardTitle className="text-sm">Policies</CardTitle>
              <CardDescription className="text-xs">Assign or remove policies for this group.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 overflow-y-auto">
                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="space-y-2 flex flex-col">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-medium text-gray-700">Copy from group</h4>
                      <span className="text-xs text-gray-400">{aliasList.length}</span>
                    </div>
                    <div className="max-h-[26vh] overflow-y-auto rounded-md border border-gray-200 bg-white">
                      {aliasList.length === 0 && (
                        <p className="px-2 py-1 text-xs text-gray-500">No groups available.</p>
                      )}
                      {aliasList.length > 0 && (
                        <div className="divide-y divide-gray-100">
                          {aliasList.map((alias) => (
                            <button
                              id={`alias-manager-copy-${toSafeId(alias.alias)}`}
                              key={`copy-${alias.alias}`}
                              type="button"
                              onClick={() => handleCopyPoliciesFromAlias(alias)}
                              className="w-full px-2 py-1.5 text-left text-xs transition-colors hover:bg-blue-50"
                            >
                              <div className="grid gap-2 text-xs text-gray-500 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,80px)]">
                                <span className="flex min-w-0 items-center gap-2 break-words text-xs font-semibold text-gray-900">
                                  <span className="min-w-0 break-words">{getAliasDisplayLabel(alias)}</span>
                                  <span className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${aliasTypeBadgeClass[resolveAliasType(alias)]}`}>
                                    {aliasTypeLabel[resolveAliasType(alias)]}
                                  </span>
                                </span>
                                <span className="min-w-0 truncate">
                                  {truncateText(alias.description || '', 50)}
                                </span>
                                <span className="min-w-0 truncate">
                                  {truncateText(buildPolicyPreview(alias.policies), 50)}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {alias.active === false ? 'Inactive' : 'Active'}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-medium text-gray-700">Available</h4>
                      <span className="text-xs text-gray-400">{availablePolicies.length}</span>
                    </div>
                    <div className="space-y-1 rounded-md border border-gray-200 bg-white p-2">
                      <label htmlFor="alias-manager-manual-policy" className="text-xs font-medium text-gray-500">
                        Add policy string
                      </label>
                      <div className="flex gap-2">
                        <Input
                          id="alias-manager-manual-policy"
                          value={manualPolicyEntry}
                          onChange={(event) => setManualPolicyEntry(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter') {
                              return;
                            }
                            event.preventDefault();
                            handleAddManualPolicy();
                          }}
                          placeholder="program:example~read"
                          className="h-8 text-sm"
                        />
                        <Button
                          id="alias-manager-add-manual-policy"
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={handleAddManualPolicy}
                        >
                          Add Policy
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Use direct entry when the builder list does not include the policy you need.
                      </p>
                    </div>
                    <div className="max-h-[34vh] overflow-y-auto rounded-md border border-gray-200 bg-gray-50">
                      {availablePolicies.length === 0 && (
                        <p className="px-2 py-1 text-xs text-gray-500">No available policies.</p>
                      )}
                      {availablePolicies.length > 0 && (
                        <div className="divide-y divide-gray-200">
                          {availablePolicies.map((policy) => (
                            <button
                              id={`alias-manager-available-${toSafeId(policy)}`}
                              key={policy}
                              type="button"
                              onClick={() => addPolicyToAlias(policy)}
                              className="w-full px-2 py-1 text-left text-xs transition-colors hover:bg-blue-50"
                            >
                              {policy}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {primitiveOptions.length === 0 && (
                      <p className="text-xs text-gray-500">
                        No policy primitives found. Manage primitives in Security Manager.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-medium text-gray-700">Included</h4>
                      <span className="text-xs text-gray-400">{selectedPolicies.length}</span>
                    </div>
                    <div className="max-h-[66vh] overflow-y-auto rounded-md border border-gray-200 bg-white">
                      {selectedPolicies.length === 0 && (
                        <p className="px-2 py-1 text-xs text-gray-500">No policies assigned.</p>
                      )}
                      {selectedPolicies.length > 0 && (
                        <div className="divide-y divide-gray-100">
                          {selectedPolicies.map((policy) => (
                            <button
                              id={`alias-manager-included-${toSafeId(policy)}`}
                              key={policy}
                              type="button"
                              onClick={() => handleRemovePolicy(policy)}
                              className="w-full px-2 py-1 text-left text-xs transition-colors hover:bg-red-50"
                            >
                              {policy}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter className="flex flex-wrap justify-end gap-2">
            <Button
              id="permission-aliases-dialog-close-button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => handleEditorOpenChange(false)}
            >
              Close
            </Button>
            <Button
              id="permission-aliases-dialog-save-button"
              type="button"
              size="sm"
              className="h-8 text-xs"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : formState.id ? 'Save Group' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionAliases;
